/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * useSelection - Custom hook to handle selection of items
 * @file useSelection.js
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import { useState, useCallback, useEffect } from 'react';
import usePrevious from './usePervious';

/**
 * useSelection - Custom hook to handle selection of items
 * @param {Array} data - Array of objects to select from
 * @param {Function} onSelect - Function to call when selection changes
 */
export default function useSelection(data, onSelect = () => {}, selectionDisableName = 'isStarted') {
  const previousData = usePrevious(data);
  const [record, setRecord] = useState(data.map((item) => ({ ...item, checked: false })));
  const resetItems = useCallback(() => {
    setRecord(data.map((item) => ({ ...item, checked: false })));
  }, [data]);

  // Execute actions when data changes
  useEffect(() => {
    if (JSON.stringify(previousData) !== JSON.stringify(data)) {
      // Reset selection state when data changes
      resetItems();
    }
  }, [data, resetItems, previousData]);

  const enabledItems = record.filter(item => !item[selectionDisableName]) || [];
  const allItemsChecked = enabledItems.length > 0 && enabledItems.every(item => item.checked);
  const someItemsChecked = enabledItems.length > 0 && enabledItems.some(item => item.checked);

  const hasAnyItemDisabled = record.some(item => item[selectionDisableName] || false);
  const allItemsDisabled = record.every(item => item[selectionDisableName] || false);

  const onSelectAndUnSelectItem = useCallback((itemId, checked) => {
    if (itemId) {
      setRecord(prevItems => {
        const targetItem = prevItems.find(item => item.id === itemId);
        // Prevent selecting/unselecting items that are started
        if (targetItem && targetItem[selectionDisableName] === true) {
          return prevItems;
        }
        const renderItems = prevItems.map(item => {
          let renderData = item;
          if (renderData.id === itemId) {
            renderData = item.checked ? { ...item, checked: false }
              : { ...item, checked: true };
          }

          return renderData;
        });

        onSelect(renderItems.filter(item => item.checked), checked);

        return renderItems;
      });
    }
  }, [onSelect, selectionDisableName]);

  const handleSelectAndUnSelectAll = (event) => {
    const { checked } = event.target;

    setRecord(prevItems => {
      const renderItems = prevItems.map(item => {
        let renderData = item;
        if (!item[selectionDisableName]) {
          renderData = { ...item, checked };
        }

        return renderData;
      });

      onSelect(renderItems.filter(item => item.checked), checked);

      return renderItems;
    });
  };

  return { record,
    setRecord,
    allItemsChecked,
    someItemsChecked,
    onSelectAndUnSelectItem,
    handleSelectAndUnSelectAll,
    resetItems,
    hasAnyItemDisabled,
    allItemsDisabled };
}
